import { getToken, clearSession } from './session';
import { fieldMessageOf } from '../i18n/el';

// Ο **μοναδικός μεταφορέας** προς το API: βασική διεύθυνση από το περιβάλλον,
// κεφαλίδα `Authorization` από τη συνεδρία, και μετάφραση κάθε απόκρισης σφάλματος
// σε `ApiError` με τη μορφή της **D4**.
//
// Ο κρίσιμος κανόνας του αρχείου είναι **πότε αποσυνδέει**: μόνο το `401` σε
// προστατευμένη διαδρομή. Βλάβη δικτύου ή `5xx` εμφανίζουν μήνυμα και αφήνουν τη
// συνεδρία **άθικτη** (ΜΛΑ-03) — αλλιώς μια στιγμιαία διακοπή θα έδιωχνε τον
// χρήστη έξω από την εφαρμογή, χάνοντας ό,τι είχε πληκτρολογήσει.

const BASE_URL = import.meta.env.VITE_API_URL;

// Διαδρομές όπου το 401 σημαίνει «λάθος στοιχεία» και όχι «η συνεδρία έληξε».
// Το API επιστρέφει 401 και για αποτυχημένη σύνδεση, οπότε χωρίς την εξαίρεση ο
// κεντρικός χειριστής θα αποσύνδεε τον χρήστη από συνεδρία που ποτέ δεν απέκτησε.
const PUBLIC_PATHS = ['/api/auth/login', '/api/auth/register'];

export class ApiError extends Error {
    constructor({ status, code, message, details }) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
}

// Το API επιστρέφει τα σφάλματα επικύρωσης ως details: [{ field, message }]. Για στοιχείο
// πίνακα το field είναι της μορφής «interests.2», οπότε καταχωρείται και η ρίζα του, ώστε
// το μήνυμα να βρίσκει το πεδίο της φόρμας.
// Τα μηνύματα του zod είναι αγγλικά και εξαρτώνται από την έκδοσή του («String
// must contain at least 8 character(s)»). Μεταφράζονται με βάση το **όνομα του
// πεδίου** και όχι το κείμενο, ώστε μια αναβάθμιση του zod να μη σπάσει τη
// μετάφραση σιωπηλά.
export function fieldErrorsOf(error) {
    if (!Array.isArray(error?.details)) return {};

    return error.details.reduce((acc, detail) => {
        const field = String(detail.field);
        // Ο **τύπος** του ζητήματος μαζί με το όνομα του πεδίου. Με μόνο το όνομα,
        // μια διεύθυνση που ήταν απλώς πολύ μεγάλη διαβαζόταν ως ελλιπής.
        const message = fieldMessageOf(field.split(".")[0], detail.code);

        acc[field] = message;

        const root = field.split(".")[0];
        if (!(root in acc)) acc[root] = message;

        return acc;
    }, {});
}

function endSession() {
    clearSession();
    // Η λήξη συνεδρίας είναι η μία περίπτωση όπου η πλήρης επαναφόρτωση είναι το
    // ζητούμενο: καταστρέφει κάθε κατάσταση και κάθε εκκρεμές αίτημα.
    window.location.replace('/login');
}

async function request(path, { method = 'GET', body, auth = true, raw = false } = {}) {
    const headers = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json; charset=utf-8';

    if (auth) {
        const token = getToken();
        if (token) headers.Authorization = `Bearer ${token}`;
    }

    let response;
    try {
        response = await fetch(`${BASE_URL}${path}`, {
            method,
            headers,
            body: body === undefined ? undefined : JSON.stringify(body)
        });
    } catch {
        // Αποτυχία μεταφοράς: το API είναι σβηστό, το δίκτυο έπεσε, ή το CORS
        // απέρριψε την προέλευση. ΔΕΝ είναι λήξη συνεδρίας (ΜΛΑ-03).
        throw new ApiError({
            status: 0,
            code: 'NETWORK_ERROR',
            message: 'Cannot reach the server. Please check your connection and try again.'
        });
    }

    if (response.status === 204) return null;

    let payload;
    try {
        const text = await response.text();
        payload = text ? JSON.parse(text) : null;
    } catch {
        throw new ApiError({
            status: response.status,
            code: 'INVALID_RESPONSE',
            message: 'The server returned an unreadable response.'
        });
    }

    // Το `raw` επιστρέφει ολόκληρο το φορτίο, ώστε να διατηρηθεί το `meta` της
    // σελιδοποίησης (Απόφαση D4). Χωρίς αυτό το `meta` χανόταν πριν φτάσει στη διεπαφή.
    if (response.ok) return raw ? payload : (payload?.data ?? null);

    throw failureOf(response, payload, path);
}

/**
 * Η αποτυχία, από **ένα** σημείο.
 *
 * Τη χρειάζονται δύο διαδρομές —η κανονική ανάγνωση και η λήψη αρχείου— και ο
 * κανόνας «401 σημαίνει λήξη συνεδρίας, εκτός από τις δημόσιες διαδρομές» δεν
 * επιτρέπεται να υπάρχει δύο φορές: γραμμένος δύο φορές είναι κανόνας που θα
 * αποκλίνει, και η απόκλιση εδώ σημαίνει χρήστης που μένει με άκυρο διακριτικό.
 */
function failureOf(response, payload, path) {
    const error = payload?.error ?? {};

    if (response.status === 401 && !PUBLIC_PATHS.includes(path)) {
        endSession();
    }

    return new ApiError({
        status: response.status,
        code: error.code ?? 'UNKNOWN_ERROR',
        message: error.message ?? `Request failed with status ${response.status}`,
        details: error.details
    });
}

/**
 * Ανάγνωση σελιδοποιημένου καταλόγου: επιστρέφει **και** το `meta`.
 *
 * Ο `get` επιστρέφει μόνο το `data`, και αυτό ήταν σφάλμα με συνέπειες. Το API
 * σελιδοποιεί από το P0 — `lib/pagination.js`, προεπιλογή 20 ανά σελίδα — και
 * στέλνει `{ page, limit, total, totalPages }` στο `meta`. Ο πελάτης το πετούσε,
 * οπότε η διεπαφή έδειχνε τους **πρώτους είκοσι** πόρους μιας συλλογής και δεν
 * είχε τρόπο να φτάσει τους υπόλοιπους: δεν ήταν ζήτημα απόδοσης αλλά **αθέατα
 * δεδομένα**, όπως και οι αρχειοθετημένες συλλογές.
 *
 * Ξεχωριστή μέθοδος και όχι αλλαγή του `get`, ώστε να μην αλλάξει η μορφή
 * επιστροφής σε είκοσι σημεία κλήσης που δεν σελιδοποιούν.
 */
async function requestPage(path) {
    const payload = await request(path, { method: 'GET', raw: true });
    return { data: payload?.data ?? [], meta: payload?.meta ?? null };
}

/**
 * **ΛΑ-28: λήψη αρχείου, και ο λόγος δεν είναι προτίμηση.**
 *
 * Η διαδρομή εξαγωγής απαιτεί `Authorization: Bearer`. Ένας απλός σύνδεσμος
 * `<a href>` **δεν στέλνει επικεφαλίδες**, οπότε ο μόνος τρόπος να δουλέψει θα
 * ήταν το διακριτικό **μέσα στη διεύθυνση** — δηλαδή σε ιστορικό περιηγητή, σε
 * `Referer` προς κάθε επόμενο προορισμό, και σε κάθε ενδιάμεσο αρχείο
 * καταγραφής. Είναι ακριβώς η κατηγορία που έκλεισε η απομάκρυνση των μυστικών
 * από τον κώδικα, σε νέα μορφή.
 *
 * Το αίτημα γίνεται επομένως εδώ, με την επικεφαλίδα, και το αποτέλεσμα
 * παραδίδεται ως αρχείο. Το **όνομα το ορίζει ο εξυπηρετητής** στο
 * `Content-Disposition` και ο πελάτης δεν το ξανακατασκευάζει: αν λείπει, πέφτει
 * σε γενικό όνομα αντί να μαντέψει την ταυτότητα του εγγράφου.
 */
async function downloadFile(path, fallbackName) {
    const headers = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    let response;
    try {
        response = await fetch(`${BASE_URL}${path}`, { headers });
    } catch {
        throw new ApiError({
            status: 0,
            code: 'NETWORK_ERROR',
            message: 'Cannot reach the server. Please check your connection and try again.'
        });
    }

    if (!response.ok) {
        let payload = null;
        try {
            const text = await response.text();
            payload = text ? JSON.parse(text) : null;
        } catch {
            payload = null;
        }
        throw failureOf(response, payload, path);
    }

    const blob = await response.blob();
    saveBlob(blob, filenameOf(response.headers.get('Content-Disposition')) || fallbackName);
}

/** Το όνομα που **δήλωσε ο εξυπηρετητής**, ή κενό αν δεν το δήλωσε. */
export function filenameOf(contentDisposition) {
    const match = /filename\s*=\s*"?([^";]+)"?/i.exec(contentDisposition ?? '');
    return match ? match[1].trim() : '';
}

function saveBlob(blob, filename) {
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Η διεύθυνση κρατά το αντικείμενο στη μνήμη μέχρι να ανακληθεί ρητά. Χωρίς
    // αυτό, κάθε εξαγωγή αφήνει πίσω της ολόκληρο το έγγραφο.
    URL.revokeObjectURL(href);
}

export const apiClient = {
    get: (path, options) => request(path, { ...options, method: 'GET' }),
    getPage: (path) => requestPage(path),
    post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
    patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
    del: (path, options) => request(path, { ...options, method: 'DELETE' }),
    download: (path, fallbackName) => downloadFile(path, fallbackName)
};

export default apiClient;
